<template>
  <component
    v-if="selectedFlow"
    :disabled="disabled"
    :disable-first-scroll-on-mount="disableFirstScrollOnMount"
    :is="selectedFlow"
    @first-mount="$emit('first-mount')"
    @appointment-selected="handleChange($event)"
  />
  <div v-show="showOkButton" class="pt-5 vff-animate f-fade-in f-enter container-x-padding">
    <button ref="button" class="o-btn-action" type="button" @click="goToConfirmation()">
      <span>{{ $t('appointment_form_page.continue') }}</span>
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent, PropType, ref, useTemplateRef, nextTick } from 'vue'
import { useAppointmentStore } from '~conciergeUiCommons/store/appointmentStore'
import { Appointment } from '~concierge/types/AppointmentTypes'
import { ConciergeAppointmentQuestionFlow } from '~types/concierge-enum'

export default defineComponent({
  name: 'AppointmentQuestions',
  components: {
    TestDriveAppointment: defineAsyncComponent(
      () =>
        import('~conciergeUiCommons/components/AppointmentQuestionComponents/TestDriveAppointment.vue')
    ),
    FreeAppointment: defineAsyncComponent(
      () => import('~concierge/components/AppointmentQuestionComponents/FreeAppointment.vue')
    ),
    AdviserSelectionAppointment: defineAsyncComponent(
      () =>
        import('~conciergeUiCommons/components/AppointmentQuestionComponents/AdviserSelectionAppointment.vue')
    )
  },
  props: {
    selectedFlow: {
      type: String as PropType<ConciergeAppointmentQuestionFlow>,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disableFirstScrollOnMount: {
      type: Boolean,
      default: false
    }
  },
  emits: ['goToConfirmation', 'first-mount'],
  setup(_, { expose }) {
    const appointmentStore = useAppointmentStore()
    const showOkButton = ref<boolean>(false)

    const buttonRef = useTemplateRef('button')
    const loaderHeight = ref<number>(100)

    async function scrollOkButtonToViewport() {
      nextTick(() => {
        buttonRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
      })
    }
    expose({ scrollOkButtonToViewport })
    return {
      appointmentStore,
      showOkButton,
      loaderHeight,
      buttonRef,
      scrollOkButtonToViewport
    }
  },
  watch: {
    selectedFlow(
      newValue: ConciergeAppointmentQuestionFlow,
      oldValue: ConciergeAppointmentQuestionFlow
    ) {
      if (newValue != oldValue) {
        this.showOkButton = false
        this.appointmentStore.resetData()
        this.appointmentStore.resetAppointment()
      }
    },
    disabled(newValue: boolean) {
      if (newValue == false) {
        this.showOkButton = false
      }
    }
  },
  methods: {
    async handleChange(appointment: Appointment): Promise<void> {
      appointment.flow = this.selectedFlow
      this.appointmentStore.setAppointment(appointment)
      this.showOkButton = true
      await this.$nextTick()
      await this.scrollOkButtonToViewport()
    },
    goToConfirmation(): void {
      this.$emit('goToConfirmation')
    }
  }
})
</script>
